import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { websiteService } from '../services/websiteService';
import type { Website } from '../types';

const STORAGE_KEY = 'foundlio-selected-website';

interface WebsiteContextValue {
  websites: Website[];
  isLoading: boolean;
  selectedWebsiteId: string | 'all';
  setSelectedWebsiteId: (id: string | 'all') => void;
  selectedWebsite: Website | null;
  isSingleWebsite: boolean;
  isAllSelected: boolean;
}

const WebsiteContext = createContext<WebsiteContextValue | null>(null);

export const WebsiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedWebsiteId, setSelectedWebsiteIdRaw] = useState<string | 'all'>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'all';
    } catch {
      return 'all';
    }
  });

  const { data: websites = [], isLoading } = useQuery({
    queryKey: ['websites'],
    queryFn: () => websiteService.getWebsites(),
    staleTime: 5 * 60 * 1000,
  });

  const setSelectedWebsiteId = useCallback((id: string | 'all') => {
    setSelectedWebsiteIdRaw(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch { /* noop */ }
  }, []);

  // Auto-select: single-site users forced to their site; invalid stored ID resets to 'all'
  useEffect(() => {
    if (websites.length === 0) return;
    if (websites.length === 1) {
      setSelectedWebsiteId(websites[0].id);
      return;
    }
    // Validate stored selection
    if (selectedWebsiteId !== 'all' && !websites.find(w => w.id === selectedWebsiteId)) {
      setSelectedWebsiteId('all');
    }
  }, [websites, selectedWebsiteId, setSelectedWebsiteId]);

  const value = useMemo<WebsiteContextValue>(() => ({
    websites,
    isLoading,
    selectedWebsiteId,
    setSelectedWebsiteId,
    selectedWebsite: selectedWebsiteId === 'all' ? null : websites.find(w => w.id === selectedWebsiteId) || null,
    isSingleWebsite: websites.length === 1,
    isAllSelected: selectedWebsiteId === 'all',
  }), [websites, isLoading, selectedWebsiteId, setSelectedWebsiteId]);

  return (
    <WebsiteContext.Provider value={value}>
      {children}
    </WebsiteContext.Provider>
  );
};

export function useWebsite(): WebsiteContextValue {
  const ctx = useContext(WebsiteContext);
  if (!ctx) throw new Error('useWebsite must be used within WebsiteProvider');
  return ctx;
}
